import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Field } from "./Shared";
import { DateField } from "./DateField";
import { Rocket, AlertTriangle, Loader2, CheckCircle2, XCircle } from "lucide-react";

const EMPTY_BATCH = { program_id: "", code: "", name: "", start_date: "", capacity: 50 };

export function GoLivePanel() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [wipeDemo, setWipeDemo] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [batch, setBatch] = useState({ ...EMPTY_BATCH });
  const [createBatch, setCreateBatch] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/admin/go-live/preview?wipe_demo_accounts=${wipeDemo}`)
      .then(({ data: res }) => setData(res.data))
      .catch((e) => toast.error(extractError(e).message));
  }, [wipeDemo]);

  const run = async () => {
    setBusy(true);
    try {
      const payload = { confirm, wipe_demo_accounts: wipeDemo };
      if (createBatch) payload.first_batch = { ...batch, capacity: Number(batch.capacity), code: batch.code.toUpperCase() };
      const { data: res } = await api.post("/admin/go-live", payload);
      setResult(res.data);
      setConfirm("");
      toast.success(t("golive.done"));
      const { data: fresh } = await api.get(`/admin/go-live/preview?wipe_demo_accounts=${wipeDemo}`);
      setData(fresh.data);
    } catch (e) { toast.error(extractError(e).message); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const c = data.counts;
  const r = data.readiness;
  const ready = [
    [t("golive.rLegal"), r.legal_configured],
    [t("golive.rPayment"), r.active_payment_account],
    [t("golive.rJourneys"), r.published_journeys > 0],
    [t("golive.rPrograms"), r.programs > 0],
  ];

  return (
    <Card data-testid="golive-panel" className="bg-[#10121B] border-[#FF3B30]/30">
      <CardContent className="p-5 space-y-5">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-white inline-flex items-center gap-2">
            <Rocket className="h-4 w-4 text-[#FF3B30]" /> {t("golive.title")}
          </h2>
          <p className="text-xs text-slate-400 mt-1">{t("golive.subtitle")}</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {[["accounts", c.accounts], ["batches", c.batches], ["programs", c.programs],
            ["journal_posts", c.journal_posts], ["measurements", c.measurements], ["files", c.files]].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-white/5 bg-white/[0.03] p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{t(`golive.c_${k}`)}</div>
              <div data-testid={`golive-count-${k}`} className="font-[Outfit] text-xl font-extrabold text-white">{v}</div>
            </div>
          ))}
        </div>

        <div className="space-y-1.5">
          {ready.map(([label, ok]) => (
            <div key={label} className="flex items-center gap-2 text-xs">
              {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <XCircle className="h-3.5 w-3.5 text-amber-400" />}
              <span className={ok ? "text-slate-300" : "text-amber-300"}>{label}</span>
            </div>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" data-testid="golive-wipe-demo" checked={wipeDemo} onChange={(e) => setWipeDemo(e.target.checked)} className="accent-[#FF3B30]" />
          {t("golive.wipeDemo")}
        </label>

        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" data-testid="golive-create-batch" checked={createBatch} onChange={(e) => setCreateBatch(e.target.checked)} className="accent-[#10B981]" />
          {t("golive.createBatch")}
        </label>

        {createBatch && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <label className="text-slate-300 text-sm">{t("golive.program")}</label>
              <select data-testid="golive-program" value={batch.program_id} onChange={(e) => setBatch({ ...batch, program_id: e.target.value })}
                className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
                <option value="">—</option>
                {data.programs_available.map((p) => <option key={p.program_id} value={p.program_id}>{p.name} ({p.duration_days} hari)</option>)}
              </select>
            </div>
            <Field label={t("golive.code")} testId="golive-code" value={batch.code} onChange={(e) => setBatch({ ...batch, code: e.target.value.toUpperCase() })} />
            <Field label={t("golive.name")} testId="golive-name" value={batch.name} onChange={(e) => setBatch({ ...batch, name: e.target.value })} />
            <DateField label={t("golive.start")} testId="golive-start" value={batch.start_date} onChange={(v) => setBatch({ ...batch, start_date: v })} clearable={false} />
            <Field label={t("golive.capacity")} testId="golive-capacity" type="number" min="1" value={batch.capacity} onChange={(e) => setBatch({ ...batch, capacity: e.target.value })} />
          </div>
        )}

        <div className="rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/[0.06] p-4 space-y-3">
          <p className="text-sm text-[#FF8A80] inline-flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {t("golive.warning")}</p>
          <Input data-testid="golive-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="GO-LIVE"
            className="bg-[#1C202E] border-white/10 text-white h-10 max-w-xs" />
          {createBatch && (!batch.program_id || !batch.code || !batch.name || !batch.start_date) && (
            <p data-testid="golive-batch-hint" className="text-xs text-amber-300">{t("golive.batchHint")}</p>
          )}
          <Button data-testid="golive-run-btn" disabled={busy || confirm.trim().toUpperCase() !== "GO-LIVE" || (createBatch && (!batch.program_id || !batch.code || !batch.name || !batch.start_date))}
            onClick={run} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold h-11">
            {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t("common.loading")}</> : <><Rocket className="h-4 w-4 mr-1" /> {t("golive.run")}</>}
          </Button>
        </div>

        {result && (
          <div data-testid="golive-result" className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.07] p-4 text-sm text-emerald-200 space-y-1">
            <div className="font-semibold">{t("golive.done")}</div>
            <div className="text-xs">{Object.entries(result.removed).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</div>
            {result.first_batch && <div className="text-xs">{t("golive.batchCreated")}: {result.first_batch.code} — {result.first_batch.name} ({result.first_batch.start_date} → {result.first_batch.end_date})</div>}
            <div className="text-xs">{t("golive.remaining")}: {Object.entries(result.remaining).map(([k, v]) => `${k} ${v}`).join(" · ")}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
